import React from 'react';
import { Brain, Code2, Database, Cloud, Play, ExternalLink } from 'lucide-react';
import { usePortfolio } from '../context/PortfolioContext';

const AboutSection = () => {
  const { personalInfo, education, certifications, loading } = usePortfolio();
  
  const highlights = [
    { icon: Brain, label: 'AI/ML', desc: 'Deep Learning & NLP' },
    { icon: Code2, label: 'Python', desc: 'TensorFlow & PyTorch' },
    { icon: Database, label: 'Data', desc: 'Analytics & Visualization' },
    { icon: Cloud, label: 'Cloud', desc: 'Azure & AWS' }
  ];

  return (
    <section id="about" className="bg-white py-24 md:py-32 relative">
      {/* Section Divider */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#e2e8f0] to-transparent" />

      <div className="max-w-[87.5rem] mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div className="mb-16">
          <span className="text-[#2563eb] text-sm uppercase tracking-widest font-semibold">Get to know me</span>
          <h2 className="text-4xl md:text-6xl font-black text-[#1e3a5f] mt-2">
            About <span className="text-[#2563eb]">Me</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left - Bio */}
          <div className="space-y-8">
            <p className="text-[#64748b] text-lg leading-relaxed">
              {personalInfo.summary}
            </p>

            {/* Highlight Cards */}
            <div className="grid grid-cols-2 gap-4">
              {highlights.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-5 hover:border-[#2563eb]/30 hover:shadow-lg hover:shadow-[#2563eb]/5 transition-all duration-300 group"
                >
                  <item.icon className="w-8 h-8 text-[#2563eb] mb-3 group-hover:scale-110 transition-transform" />
                  <h4 className="text-[#1e3a5f] font-semibold text-lg">{item.label}</h4>
                  <p className="text-[#64748b] text-sm mt-1">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* As Seen In */}
            <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2563eb] rounded-2xl p-6">
              <p className="text-white/70 text-xs uppercase tracking-widest mb-3">As Seen In</p>
              <a
                href="https://youtu.be/mzL0O6e0IAs?si=c9eK3GPDpTqDC1yg"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 group"
              >
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-white/20 transition-colors">
                  <Play className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-semibold text-sm leading-tight group-hover:text-white/90 transition-colors">
                    The Future of AI | How AI will rule in the next decade
                  </h4>
                  <p className="text-white/60 text-xs mt-1">AI Industry Expert - Eduleem Podcast</p>
                </div>
                <ExternalLink className="w-4 h-4 text-white/50 group-hover:text-white transition-colors flex-shrink-0 mt-1" />
              </a>
            </div>
          </div>

          {/* Right - Education & Certifications */}
          <div className="space-y-8">
            {/* Education */}
            <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-8">
              <h3 className="text-[#2563eb] font-bold text-xl mb-6 uppercase tracking-wide">Education</h3>
              {education.map((edu, index) => (
                <div key={index} className="space-y-2">
                  <h4 className="text-[#1e3a5f] font-semibold text-lg">{edu.degree}</h4>
                  <p className="text-[#64748b]">{edu.institution}</p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-[#2563eb] font-semibold">{edu.year}</span>
                    <span className="text-[#94a3b8]">•</span>
                    <span className="text-[#64748b]">GPA: {edu.gpa}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Certifications */}
            <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-8">
              <h3 className="text-[#2563eb] font-bold text-xl mb-6 uppercase tracking-wide">Certifications</h3>
              <div className="space-y-4">
                {certifications.map((cert, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 bg-white rounded-xl border border-[#e2e8f0]"
                  >
                    <span className="text-[#1e3a5f] font-medium">{cert.name}</span>
                    <span className="text-[#2563eb] text-xs uppercase bg-[#2563eb]/10 px-3 py-1 rounded-full font-semibold">
                      {cert.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-[#f8fafc] rounded-xl border border-[#e2e8f0]">
                <span className="text-[#2563eb] text-3xl font-black">4+</span>
                <p className="text-[#64748b] text-sm mt-1">Years Exp</p>
              </div>
              <div className="text-center p-4 bg-[#f8fafc] rounded-xl border border-[#e2e8f0]">
                <span className="text-[#2563eb] text-3xl font-black">10+</span>
                <p className="text-[#64748b] text-sm mt-1">Projects</p>
              </div>
              <div className="text-center p-4 bg-[#f8fafc] rounded-xl border border-[#e2e8f0]">
                <span className="text-[#2563eb] text-3xl font-black">3</span>
                <p className="text-[#64748b] text-sm mt-1">Certs</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
